import type { ReactNode } from 'react';
import type { Options as MarkdownOptions } from 'react-markdown';
import type { QuickChartData } from '@trinity/chat';
import { BasicMarkdown } from '@trinity/chat-components/components/Markdown/Markdown';
import { ChartVisualizationUI } from '@trinity/chat-components/components/Widgets/ChartWidgetRouter';
import {
  MetricCard,
  MetricCardGrid,
  MetricLabel,
  MetricValue,
  StyledList,
  StyledListItem,
  StyledProfileWidgetP,
} from './shared-styles';

export function renderMetricCard(innerData: Record<string, unknown>): ReactNode {
  const values = (innerData.values ?? []) as string[];
  const labels = (innerData.labels ?? []) as string[];
  const kpis = (innerData.kpis ?? []) as Array<{ label: string; value: string; unit: string }>;

  const items =
    kpis.length > 0
      ? kpis.map((kpi, i) => ({ key: i, label: kpi.label, display: `${kpi.value}${kpi.unit}` }))
      : values.map((val, i) => ({ key: i, label: labels[i] ?? '', display: val }));

  return (
    <MetricCardGrid>
      {items.map((item) => (
        <MetricCard key={item.key}>
          <MetricLabel>{item.label}</MetricLabel>
          <MetricValue>{item.display}</MetricValue>
        </MetricCard>
      ))}
    </MetricCardGrid>
  );
}

export function renderProfileWidget(widget: { id: string; type: string; data: unknown }): ReactNode {
  const data = widget.data as Record<string, unknown> | null;
  if (!data) {
    return null;
  }

  const dataType = data.type as string;
  const innerData = data.data as Record<string, unknown> | undefined;

  if (dataType === 'metric_card' && innerData) {
    return renderMetricCard(innerData);
  }

  if (dataType === 'chart' && innerData) {
    return <ChartVisualizationUI chartData={innerData as QuickChartData} />;
  }

  if (dataType === 'text' && innerData) {
    const content = (innerData.content ?? innerData.markdownContent ?? '') as string;

    const profileWidgetMarkdownComponents: MarkdownOptions['components'] = {
      p: (props) => <StyledProfileWidgetP>{props.children}</StyledProfileWidgetP>,
      ul: (props) => <StyledList>{props.children}</StyledList>,
      li: (props) => <StyledListItem>{props.children}</StyledListItem>,
    };

    return <BasicMarkdown components={profileWidgetMarkdownComponents}>{content}</BasicMarkdown>;
  }

  return null;
}
